/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import {
  Checkbox, Col, Inline, Row, SearchInput, Typography, Spinner, Button,
} from '@airbus/components-react';
import { Reset } from '@airbus/icons/react';
import './AdvanceComboBox.scss';
import AdvanceComboBoxList from './AdvanceComboBoxList';

// Props for AdvanceComboBoxContent component
interface AdvanceComboBoxContentProps {
  showApply: boolean;
  loading: boolean;
  search: string;
  setSearch: (val: string) => void;
  handleOptionChange: (option: AcbObjectType) => void;
  handleSelectAll: (selectAll: boolean) => void;
  handleClear: (clearAll: boolean) => void;
  setOpen: (open: boolean) => void;
  listHeight: number;
  rowHeight: number;
  virtualizationThreshold: number;
  filterOptions: Array<AcbObjectType>;
  searchPattern: 'contains' | 'startsWith';
}

/**
 * AdvanceComboBoxContent
 * -----------------------------------------------------------------------------
 * This component renders the content of the AdvanceComboBox dropdown.
 *
 * Features:
 * - Displays a search input for filtering options.
 * - Supports multi-select actions: "Select All", "Deselect All", and "Clear".
 * - Shows a virtualized or regular list of options depending on the number of items.
 * - Displays a loading spinner while options are being loaded.
 * - Provides "Cancel" and "Apply" footer actions for multi-select scenarios.
 *
 * Props:
 * - showApply: Shows the Apply/Cancel actions in the footer.
 * - loading: Indicates if options are being loaded.
 * - search: Current search input value.
 * - setSearch: Function to update the search value.
 * - filteredOptions: Array of options to display.
 * - selectedOptions: Array of currently selected options.
 * - handleOptionChange: Handler for selecting/deselecting an option.
 * - handleSelectAll: Handler for toggling select all/deselect all.
 * - handleClear: Handler for clearing all selections.
 * - handleApply: Handler for applying the selection.
 * - setOpen: Function to open/close the dropdown.
 * - listHeight, rowHeight, virtualizationThreshold: Control list rendering and virtualization.
 *
 * This component is used internally by AdvanceComboBox to render its dropdown UI.
 */
const AdvanceComboBoxContent: React.FC<AdvanceComboBoxContentProps> = ({
  showApply,
  loading,
  search,
  setSearch,
  handleOptionChange,
  handleSelectAll,
  handleClear,
  setOpen,
  listHeight,
  rowHeight,
  virtualizationThreshold,
  filterOptions,
  searchPattern,
}) => {
  // Filter options based on search and searchPattern
  const filteredOptions = React.useMemo(() => {
    if (!search) return filterOptions;
    if (searchPattern === 'startsWith') {
      return filterOptions.filter((item) => item.name.toLowerCase().startsWith(search.toLowerCase()));
    }
    // default to contains
    return filterOptions.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()));
  }, [search, filterOptions, searchPattern]);

  const selectAllState = filteredOptions.length > 0 && filteredOptions.every((item) => item.checked);
  const clearAllState = filteredOptions.map((item) => (item.checked ? { ...item, checked: false } : item));
  const selectAllLabel = search ? 'Select Filtered' : 'Select All';
  return (
    <div className="advance-combo-content" role="presentation" onMouseDown={(e) => e.stopPropagation()}>
      {/* Search input for filtering options */}
      <SearchInput
        placeholder="Search"
        data-testid="ACB-Search-Input"
        value={search}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(_, value) => setSearch(value)}
        disabled={loading}
        autoFocus
      />
      {/* Multi-select actions: Select All/Deselect All and Clear */}
      <Row className="advance-combo-actions" onMouseDown={(e) => e.stopPropagation()}>
        <Col>
          {/* Checkbox toggles select all/deselect all */}
          <Checkbox
            label={selectAllLabel}
            onMouseDown={(e) => e.stopPropagation()}
            checked={selectAllState}
            onChange={() => handleSelectAll(!selectAllState)}
            disabled={filteredOptions.length === 0}
            data-testid="ACB-Select-All-Checkbox"
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        </Col>
        <Col>
          {/* Inline clear action and reset icon */}
          <Inline>
            <Typography
              variant="small"
              className="advance-combo-clear"
              onClick={() => { handleClear(!clearAllState); }}
              data-testid="ACB-Clear-button"
            >
              Clear
            </Typography>
            <div className="advance-combo-reset-icon">
              <Reset onClick={() => handleClear(!clearAllState)} data-testid="reset-icon" />
            </div>
          </Inline>
        </Col>
      </Row>
      {/* List of options or loading spinner */}
      <div className="advance-combo-list" role="presentation" onMouseDown={(e) => e.stopPropagation()}>
        {loading ? (
          // Show spinner while loading options
          <div className="advance-combo-spinner">
            <Spinner size="medium" />
          </div>
        ) : (
          // Render the list of options
          <AdvanceComboBoxList
            handleOptionChange={handleOptionChange}
            listHeight={listHeight}
            rowHeight={rowHeight}
            virtualizationThreshold={virtualizationThreshold}
            filterOptions={filteredOptions}
          />
        )}
      </div>
      {/* Footer actions: Cancel and Apply (only for multi-select with showApply) */}
      {showApply && (
        <Row className="advance-combo-footer">
          <Col>
            {/* Cancel button closes the dropdown */}
            <Button
              size="small"
              variant="secondary"
              onClick={() => setOpen(false)}
              className="combo-cancel-button"
              placeholder={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            >
              Cancel
            </Button>
          </Col>
          <Col>
            {/* Apply button confirms selection, disabled if nothing is selected */}
            <Button
              size="small"
              variant="primary"
              disabled={filteredOptions.length === 0}
              className="combo-apply-button"
              placeholder={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            >
              Apply
            </Button>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default AdvanceComboBoxContent;
